import React, { Fragment, useState } from 'react';
import './index.scss';

interface TwoMonthComparisonProps {
  currentMonth: {
    referralRequests: number;
    closedCases: number;
  };
  lastMonth: {
    referralRequests: number;
    closedCases: number;
  };
  loading?: boolean;
}

const TwoMonthComparisonChart: React.FC<TwoMonthComparisonProps> = ({
  currentMonth,
  lastMonth,
  loading = false,
}) => {
  const [hoveredBar, setHoveredBar] = useState<string | null>(null);

  // Format number with commas
  const formatNumber = (num: number): string => {
    return num.toLocaleString('en-US');
  };

  // Calculate bar heights as percentages - normalize per chart group
  const getBarHeight = (value: number, maxForGroup: number) => {
    const max = Math.max(maxForGroup, 1); // Avoid division by zero
    return (value / max) * 100;
  };

  // Get max values for each chart group
  const referralRequestsMax = Math.max(
    currentMonth.referralRequests,
    lastMonth.referralRequests,
    1
  );
  const closedCasesMax = Math.max(
    currentMonth.closedCases,
    lastMonth.closedCases,
    1
  );

  const currentDate = new Date();
  const currentMonthName = currentDate.toLocaleDateString('en-US', {
    month: 'long',
  });
  const lastMonthDate = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() - 1,
    1
  );
  const lastMonthName = lastMonthDate.toLocaleDateString('en-US', {
    month: 'long',
  });

  if (loading) {
    return (
      <div className="two-month-comparison">
        <h3 className="comparison-title">
          Case Activity: Two-Month Comparison
        </h3>
        <div className="comparison-charts">
          <div className="chart-group">
            <div className="skeleton skeleton-title"></div>
            <div className="bar-chart">
              <div className="bar-wrapper">
                <div className="skeleton skeleton-bar"></div>
              </div>
              <div className="bar-wrapper">
                <div className="skeleton skeleton-bar"></div>
              </div>
            </div>
          </div>
          <div className="chart-group">
            <div className="skeleton skeleton-title"></div>
            <div className="bar-chart">
              <div className="bar-wrapper">
                <div className="skeleton skeleton-bar"></div>
              </div>
              <div className="bar-wrapper">
                <div className="skeleton skeleton-bar"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="two-month-comparison">
      <h3 className="comparison-title">Case Activity: Two-Month Comparison</h3>
      <div className="comparison-charts">
        {/* Referral Requests Chart */}
        <div className="chart-group">
          <h4 className="chart-title">Submitted Requests</h4>

          <div className="bar-chart">
            <Fragment>
              <div className="bar-wrapper">
                <div className="bar-container">
                  <div className="bar-value">
                    {formatNumber(lastMonth.referralRequests)}
                  </div>
                  <div
                    className="bar bar-gray"
                    style={{
                      height: `${getBarHeight(
                        lastMonth.referralRequests,
                        referralRequestsMax
                      )}%`,
                    }}
                    onMouseEnter={() => setHoveredBar('lastReferrals')}
                    onMouseLeave={() => setHoveredBar(null)}
                  >
                    <div className="bar-inner"></div>
                    {hoveredBar === 'lastReferrals' && (
                      <div className="bar-tooltip">
                        {lastMonthName}:{' '}
                        {formatNumber(lastMonth.referralRequests)} referral
                        requests
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="bar-wrapper">
                <div className="bar-container">
                  <div className="bar-value">
                    {formatNumber(currentMonth.referralRequests)}
                  </div>
                  <div
                    className="bar bar-blue"
                    style={{
                      height: `${getBarHeight(
                        currentMonth.referralRequests,
                        referralRequestsMax
                      )}%`,
                    }}
                    onMouseEnter={() => setHoveredBar('currentReferrals')}
                    onMouseLeave={() => setHoveredBar(null)}
                  >
                    <div className="bar-inner"></div>
                    {hoveredBar === 'currentReferrals' && (
                      <div className="bar-tooltip">
                        {currentMonthName}:{' '}
                        {formatNumber(currentMonth.referralRequests)} referral
                        requests
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Fragment>
          </div>
          <div className="chart-baseline"></div>
        </div>

        {/* Closed Cases Chart */}
        <div className="chart-group">
          <h4 className="chart-title">Closed Cases</h4>
          <div className="bar-chart">
            <Fragment>
              <div className="bar-wrapper">
                <div className="bar-container">
                  <div className="bar-value">
                    {formatNumber(lastMonth.closedCases)}
                  </div>
                  <div
                    className="bar bar-gray"
                    style={{
                      height: `${getBarHeight(
                        lastMonth.closedCases,
                        closedCasesMax
                      )}%`,
                    }}
                    onMouseEnter={() => setHoveredBar('lastClosed')}
                    onMouseLeave={() => setHoveredBar(null)}
                  >
                    <div className="bar-inner"></div>
                    {hoveredBar === 'lastClosed' && (
                      <div className="bar-tooltip">
                        {lastMonthName}: {formatNumber(lastMonth.closedCases)}{' '}
                        closed cases
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="bar-wrapper">
                <div className="bar-container">
                  <div className="bar-value">
                    {formatNumber(currentMonth.closedCases)}
                  </div>
                  <div
                    className="bar bar-orange"
                    style={{
                      height: `${getBarHeight(
                        currentMonth.closedCases,
                        closedCasesMax
                      )}%`,
                    }}
                    onMouseEnter={() => setHoveredBar('currentClosed')}
                    onMouseLeave={() => setHoveredBar(null)}
                  >
                    <div className="bar-inner"></div>
                    {hoveredBar === 'currentClosed' && (
                      <div className="bar-tooltip">
                        {currentMonthName}:{' '}
                        {formatNumber(currentMonth.closedCases)} closed cases
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Fragment>
          </div>
          <div className="chart-baseline"></div>
        </div>
      </div>
    </div>
  );
};

export default TwoMonthComparisonChart;
